// Commission-scenario comparison (Performance tab, below the main TWR graph).
//
// Shows the account's TWR under four commission models — Book (what the NAV/TWR deducts: the Tiered
// model before the account's Tiered-retro boundary, the actual IBKR charge after it — matches the graph
// above) / Actual paid (what IBKR charged; the Fixed-era overpayment vs Tiered was refunded as capital, so
// this is a reference step-down) / Tiered (hypothetical throughout) / At-scale ($0.0035/share, min never
// binds) — across EVERY range + custom. It fetches four full inception→today curves once and rebases
// each with the SAME twrRange helpers as the main graph, so the "Book" column always equals the page's
// headline TWR. Nothing here changes real records or the feed.
//
// Single-account only (commissions are per-account) — the page passes soloAccountId or nothing.
import React, { useEffect, useMemo, useState } from 'react';
import { tradingService } from '../../../services/tradingService';
import { RANGES, RANGE_LABELS, rebaseSeries, rebaseCustom } from './twrRange';

const pct = (v, dp = 2) => (v == null || isNaN(v) ? '—' : `${(v * 100).toFixed(dp)}%`);
const bps = (v) => (v == null || isNaN(v) ? '—' : `${v >= 0 ? '+' : ''}${(v * 10000).toFixed(1)}`);
const sign = (v) => (v == null || isNaN(v) ? 'text-gray-400' : v >= 0 ? 'text-emerald-500' : 'text-red-500');
const usd = (v) => (v == null || isNaN(v) ? '—' : `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}`);

export default function CommissionScenarioPanel({ accountId, range, customStart, customEnd, asOf }) {
  const [open, setOpen] = useState(true);
  const [liquidity, setLiquidity] = useState('remove');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (accountId == null) { setData(null); return undefined; }
    let alive = true;
    setLoading(true); setError(null);
    tradingService.getCommissionScenarioTwr(accountId, liquidity)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.error || 'Failed to load commission scenarios'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [accountId, liquidity]);

  const curves = useMemo(() => data?.curves || {}, [data]);
  const stamp = asOf || data?.as_of || null;
  const hasData = !!(curves.book && curves.book.length);

  // Windowed return per (model × range) — the SAME rebase the main graph uses, so Actual matches it.
  const rows = useMemo(() => {
    const list = RANGES.map((r) => ({ key: r, label: RANGE_LABELS[r] || r, custom: false }));
    if (customStart || customEnd) list.push({ key: 'CUSTOM', label: 'Custom', custom: true });
    return list.map((row) => {
      const win = (m) => {
        const s = curves[m] || [];
        if (!s.length) return null;
        const w = row.custom ? rebaseCustom(s, customStart, customEnd) : rebaseSeries(s, row.key, stamp);
        return w.windowReturn;
      };
      const b = win('book'); const a = win('actual'); const t = win('tiered'); const sc = win('at_scale');
      const d = (x) => (x != null && b != null) ? x - b : null;
      return {
        ...row, book: b, actual: a, tiered: t, at_scale: sc,
        dActual: d(a), dTiered: d(t), dScale: d(sc),
      };
    });
  }, [curves, customStart, customEnd, stamp]);

  if (accountId == null) return null;   // single-account only

  const comm = data?.commission || {};

  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between text-left">
        <div className="text-[10px] uppercase tracking-wider text-gray-400">
          Commission scenarios · Book vs Actual paid vs Tiered vs At-scale
          {loading && <span className="ml-2 inline-block w-3 h-3 border-2 border-brand-gold/70 border-t-transparent rounded-full animate-spin align-middle" />}
        </div>
        <span className="text-gray-400 text-xs">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
            TWR under each commission model. <span className="font-medium">Book</span> = what the NAV/TWR deducts
            (Tiered model before {data?.tiered_from || 'the Tiered switch'}, actual IBKR charge after) — matches the graph above.
            <span className="font-medium"> Actual paid</span> = Fixed-era commissions as charged; the overpayment vs Tiered was
            refunded by the owner&apos;s true-up deposit (see Infusion Manager) — shown for reference.
            <span className="font-medium"> Tiered</span> = the Tiered model throughout ($0.35/order min + pass-throughs).
            <span className="font-medium"> At-scale</span> = $0.0035/share, the per-order minimum never binds.
            <span className="italic"> Tiered &amp; At-scale are hypothetical — real records and the reported number are unchanged.</span>
          </p>

          {/* liquidity assumption for the hypothetical exchange leg */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-gray-400">Exchange fees</span>
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden text-xs">
              {[['remove', 'Remove liq (market)'], ['add', 'Add liq (rebate)']].map(([m, lab]) => (
                <button key={m} onClick={() => setLiquidity(m)}
                  className={`px-3 py-1 ${liquidity === m ? 'bg-brand-gold/10 text-brand-gold font-semibold' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
                  {lab}
                </button>
              ))}
            </div>
          </div>

          {error && <div className="text-sm text-red-500">{error}</div>}
          {!error && !hasData && !loading && (
            <p className="text-sm text-gray-400 italic">No strategy fills on this account yet — nothing to compare.</p>
          )}

          {hasData && (
            <>
              {/* ITD commission $ paid vs hypothetical */}
              <div className="text-[11px] text-gray-500 dark:text-gray-400">
                Inception-to-date commission ({data?.n_orders || 0} orders / {Number(data?.shares || 0).toLocaleString()} sh):
                <span className="ml-1 tabular-nums">Book <span className="font-medium text-gray-700 dark:text-gray-200">{usd(comm.book)}</span></span>
                <span className="ml-2 tabular-nums">· Actual paid <span className="font-medium text-gray-700 dark:text-gray-200">{usd(comm.actual)}</span></span>
                <span className="ml-2 tabular-nums">· Tiered <span className="font-medium text-gray-700 dark:text-gray-200">{usd(comm.tiered)}</span></span>
                <span className="ml-2 tabular-nums">· At-scale <span className="font-medium text-gray-700 dark:text-gray-200">{usd(comm.at_scale)}</span></span>
              </div>

              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full text-[12px] min-w-[720px]">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-800">
                      <th className="py-2 px-3 text-left">Period</th>
                      <th className="py-2 px-3 text-right">Book TWR</th>
                      <th className="py-2 px-3 text-right">Actual paid</th>
                      <th className="py-2 px-3 text-right">Tiered</th>
                      <th className="py-2 px-3 text-right">At-scale</th>
                      <th className="py-2 px-3 text-right">Actual Δ</th>
                      <th className="py-2 px-3 text-right">Tiered Δ</th>
                      <th className="py-2 px-3 text-right">At-scale Δ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60">
                    {rows.map((r) => {
                      const active = r.key === range || (r.custom && range === 'CUSTOM');
                      return (
                        <tr key={r.key} className={active ? 'bg-brand-gold/5' : 'hover:bg-gray-50 dark:hover:bg-gray-800/30'}>
                          <td className={`py-1.5 px-3 ${active ? 'text-brand-gold font-semibold' : 'text-gray-600 dark:text-gray-300'}`}>{r.label}</td>
                          <td className={`py-1.5 px-3 text-right tabular-nums ${sign(r.book)}`}>{pct(r.book)}</td>
                          <td className={`py-1.5 px-3 text-right tabular-nums ${sign(r.actual)}`}>{pct(r.actual)}</td>
                          <td className={`py-1.5 px-3 text-right tabular-nums ${sign(r.tiered)}`}>{pct(r.tiered)}</td>
                          <td className={`py-1.5 px-3 text-right tabular-nums ${sign(r.at_scale)}`}>{pct(r.at_scale)}</td>
                          <td className={`py-1.5 px-3 text-right tabular-nums ${sign(r.dActual)}`}>{bps(r.dActual)}<span className="text-gray-400 text-[10px]"> bps</span></td>
                          <td className={`py-1.5 px-3 text-right tabular-nums ${sign(r.dTiered)}`}>{bps(r.dTiered)}<span className="text-gray-400 text-[10px]"> bps</span></td>
                          <td className={`py-1.5 px-3 text-right tabular-nums ${sign(r.dScale)}`}>{bps(r.dScale)}<span className="text-gray-400 text-[10px]"> bps</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-gray-400">
                Δ = model TWR − Book TWR, in basis points, per window. Book deducts the Tiered model before the
                Tiered switch and the actual IBKR charge after it; Actual paid is what IBKR charged — the Fixed-era
                difference was refunded as capital (Infusion Manager), so it appears here only as a reference step-down.
                Post-switch, Tiered vs Book measures only rebates netted in IBKR&apos;s report (≈ 0 bps).
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
